import { IMPORT_INTERVALS } from "common";
import type {
  VmetaImportConfigCreateBody,
  VmetaImportConfigDetail,
  VmetaImportConfigEditableBody,
  VmetaImportConfigUpdateBody,
  VmetaImportMapping,
  VmetaImportPlatform,
  VmetaImportPreviewState,
} from "common";

export const STALE_CREDENTIAL_MESSAGE =
  "This credential changed since you opened it. Close this drawer and reload the list, then try again.";

/** A 409 means the credential changed under the form, so always say to reload, even without a server message. */
export const credentialErrorMessage = (
  error: unknown,
  fallback: string,
  serverMessage: (error: unknown, fallback: string) => string
): string =>
  (error as { response?: { status?: number } })?.response?.status === 409
    ? serverMessage(error, STALE_CREDENTIAL_MESSAGE)
    : serverMessage(error, fallback);

/** The number of configs a confirmed address change would move, when the server asked for that confirmation. */
export const boundConfigsCount = (error: unknown): number | null => {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { code?: unknown; count?: unknown };
      };
    }
  )?.response;
  return response?.status === 409 &&
    response.data?.code === "BOUND_CONFIGS" &&
    typeof response.data.count === "number"
    ? response.data.count
    : null;
};

export const PREVIEW_POLL_MS = 2000;
export const KEY_WARNING_DAYS = 30;

const PLATFORM_LABELS: Record<string, string> = {
  freezerpro: "FreezerPro",
  mydatahelps: "MyDataHelps",
};
export const platformLabel = (platform: string | null): string =>
  platform
    ? (PLATFORM_LABELS[platform] ?? `${platform} (read only)`)
    : "No platform (read only)";

const plural = (n: number, unit: string): string =>
  n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`;
function minutesLabel(minutes: number): string {
  if (minutes % 10080 === 0) return plural(minutes / 10080, "week");
  if (minutes % 1440 === 0) return plural(minutes / 1440, "day");
  if (minutes % 60 === 0) return plural(minutes / 60, "hour");
  return `Every ${minutes} minutes`;
}

/** The schedule choices, in minutes with labels, derived from the one list in common. */
export const IMPORT_INTERVAL_OPTIONS = IMPORT_INTERVALS.map(minutes => ({
  value: minutes,
  label: minutesLabel(minutes),
}));

export const intervalLabel = (minutes: number | null): string =>
  minutes === null ? "Not set" : minutesLabel(minutes);

/** Tailwind classes for a key expiry: amber within KEY_WARNING_DAYS, red once expired. Shared by the list and the key panel. */
export const KEY_TONE_CLASS: Record<
  "none" | "ok" | "soon" | "expired",
  string
> = {
  none: "text-gray-500",
  ok: "text-gray-700",
  soon: "text-amber-700 font-semibold",
  expired: "text-red-700 font-semibold",
};

export type FieldPair = { db: string; api: string };
export type ParentRefRow = { collection: string; field: string; api: string };
export type ParentUpdateRow = {
  collection: string;
  field: string;
  source: "apiField" | "string";
  value: string;
};

export interface ImportFormValues {
  name: string;
  platform: VmetaImportPlatform;
  /** import_credential.id the import signs in with. Null until one is chosen. */
  credentialId: number | null;
  apiUrl: string;
  apiMethod: "GET" | "POST";
  dataLocation: string;
  nextPageLocation: string;
  nextPageParameter: string;
  collectionID: string;
  refDB: string;
  refAPI: string;
  fields: FieldPair[];
  parentRefs: ParentRefRow[];
  parentUpdates: ParentUpdateRow[];
  deletedField: string;
  autoUpdate: boolean;
  autoUpdateInterval: number;
}

export const emptyFormValues = (
  platform: VmetaImportPlatform
): ImportFormValues => ({
  name: "",
  platform,
  credentialId: null,
  apiUrl: "",
  apiMethod: "GET",
  dataLocation: "",
  nextPageLocation: "",
  nextPageParameter: "",
  collectionID: "",
  refDB: "",
  refAPI: "",
  fields: [],
  parentRefs: [],
  parentUpdates: [],
  deletedField: "",
  autoUpdate: false,
  autoUpdateInterval: 60,
});

const at = (list: string[] | undefined, index: number): string =>
  list?.[index] ?? "";

export function toFormValues(
  detail: VmetaImportConfigDetail
): ImportFormValues {
  const m = detail.mapping;
  return {
    name: detail.name,
    platform: detail.platform === "mydatahelps" ? "mydatahelps" : "freezerpro",
    credentialId: detail.credentialId,
    apiUrl: detail.apiUrl ?? "",
    apiMethod: detail.apiMethod?.toUpperCase() === "POST" ? "POST" : "GET",
    dataLocation: detail.data_location ?? "",
    nextPageLocation: detail.next_page_location ?? "",
    nextPageParameter: detail.next_page_parameter ?? "",
    collectionID: m?.collectionID ?? "",
    refDB: m?.refDB ?? "",
    refAPI: m?.refAPI ?? "",
    fields: (m?.selectDB ?? []).map((db, i) => ({
      db,
      api: at(m?.selectAPI, i),
    })),
    parentRefs: (m?.selectParentRefDBCollection ?? []).map((collection, i) => ({
      collection,
      field: at(m?.selectParentRefDBField, i),
      api: at(m?.selectParentRefAPI, i),
    })),
    parentUpdates: (m?.selectParentUpdateDBCollection ?? []).map(
      (collection, i) => {
        const source = m?.selectParentUpdateAPI?.[i];
        return {
          collection,
          field: at(m?.selectParentUpdateDBField, i),
          source:
            typeof source === "object" && source?.type === "string"
              ? "string"
              : "apiField",
          value: typeof source === "string" ? source : (source?.value ?? ""),
        };
      }
    ),
    deletedField: m?.selectDeletedField ?? "",
    autoUpdate: detail.autoUpdate,
    autoUpdateInterval: detail.autoUpdateInterval ?? 60,
  };
}

function toMapping(v: ImportFormValues): VmetaImportMapping | null {
  if (!v.collectionID) return null;
  const fields = v.fields.filter(f => f.db && f.api);
  const refs = v.parentRefs.filter(r => r.collection && r.field && r.api);
  const updates = v.parentUpdates.filter(
    u => u.collection && u.field && u.value
  );
  return {
    collectionID: v.collectionID,
    refDB: v.refDB,
    refAPI: v.refAPI,
    selectDB: fields.map(f => f.db),
    selectAPI: fields.map(f => f.api),
    selectDeletedField: v.deletedField,
    selectParentRefDBCollection: refs.map(r => r.collection),
    selectParentRefDBField: refs.map(r => r.field),
    selectParentRefAPI: refs.map(r => r.api),
    selectParentUpdateDBCollection: updates.map(u => u.collection),
    selectParentUpdateDBField: updates.map(u => u.field),
    selectParentUpdateAPI: updates.map(u => ({
      type: u.source,
      value: u.value,
    })),
  };
}

function editableBody(v: ImportFormValues): VmetaImportConfigEditableBody {
  return {
    name: v.name,
    ...(v.credentialId !== null ? { credentialId: v.credentialId } : {}),
    apiUrl: v.apiUrl,
    apiMethod: v.apiMethod,
    data_location: v.dataLocation,
    next_page_location: v.nextPageLocation,
    next_page_parameter: v.nextPageParameter,
    autoUpdate: v.autoUpdate,
    autoUpdateInterval: v.autoUpdateInterval,
    mapping: toMapping(v),
  };
}

export const toCreateBody = (
  v: ImportFormValues
): VmetaImportConfigCreateBody => ({
  ...editableBody(v),
  name: v.name,
  oauth2_platform: v.platform,
  credentialId: v.credentialId as number,
});

export const toUpdateBody = (
  v: ImportFormValues,
  lastUpdateDate: string | null
): VmetaImportConfigUpdateBody => ({
  ...editableBody(v),
  lastUpdateDate,
});

export type KeyExpiryTone = "none" | "ok" | "soon" | "expired";

export function keyExpiryTone(
  keyExpiresAt: string | null,
  now: Date = new Date()
): KeyExpiryTone {
  if (!keyExpiresAt) return "none";
  const left = new Date(keyExpiresAt).getTime() - now.getTime();
  if (Number.isNaN(left)) return "none";
  if (left <= 0) return "expired";
  return left <= KEY_WARNING_DAYS * 24 * 60 * 60 * 1000 ? "soon" : "ok";
}

/** Poll every 2 s while the preview runs (or has not been read yet); stop once it settles or is lost. */
export function previewRefetchInterval(
  state: VmetaImportPreviewState | undefined,
  isError: boolean
): number | false {
  if (isError) return false;
  return state === undefined || state.status === "running"
    ? PREVIEW_POLL_MS
    : false;
}
